import React, { Children } from 'react';
import { Container } from '../components/ui/Container';
export function PrivacyPage() {
  return <div className="bg-white py-20 lg:py-28" data-id="element-1899">
      <Container size="md" data-id="element-1900">
        <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl mb-8" data-id="element-1901">
          Privacy Policy
        </h1>
        <p className="text-gray-500 mb-12" data-id="element-1902">Last updated: December 15, 2024</p>

        <div className="prose prose-lg prose-indigo max-w-none" data-id="element-1903">
          <p className="text-xl text-gray-600 mb-8" data-id="element-1904">
            At Flowsense ("we", "us", or "our"), operated by Acciom Limited, we
            respect your privacy and are committed to protecting your personal
            data. This privacy policy explains how we collect, use, disclose,
            and safeguard your information when you use our service.
          </p>

          <div className="bg-indigo-50 border-l-4 border-indigo-500 p-6 mb-8" data-id="element-1905">
            <p className="text-sm text-indigo-900 font-semibold mb-2" data-id="element-1906">
              KEY PRIVACY COMMITMENTS
            </p>
            <ul className="text-sm text-indigo-800 space-y-1 mb-0" data-id="element-1907">
              <li data-id="element-1908">
                • We only access Klaviyo data you explicitly authorize via OAuth
              </li>
              <li data-id="element-1909">
                • We never store customer PII (Personally Identifiable
                Information) from your Klaviyo account
              </li>
              <li data-id="element-1910">
                • We use industry-standard encryption for all data in transit
                and at rest
              </li>
              <li data-id="element-1911">• We never sell your data to third parties</li>
              <li data-id="element-1912">• You can delete your data at any time</li>
            </ul>
          </div>

          <h2 data-id="element-1913">1. Information We Collect</h2>

          <h3 data-id="element-1914">1.1 Information You Provide to Us</h3>
          <p data-id="element-1915">
            When you register for and use Flowsense, we collect the following
            information:
          </p>
          <ul data-id="element-1916">
            <li data-id="element-1917">
              <strong data-id="element-1918">Account Information:</strong> Name, email address,
              password (encrypted), company name, and billing information
            </li>
            <li data-id="element-1919">
              <strong data-id="element-1920">Client Information:</strong> Client names, website URLs,
              and optional notes you add about your clients
            </li>
            <li data-id="element-1921">
              <strong data-id="element-1922">Payment Information:</strong> Credit card details
              (processed securely through Stripe; we do not store full card
              numbers)
            </li>
            <li data-id="element-1923">
              <strong data-id="element-1924">Communications:</strong> Messages you send to our support
              team, feedback, and survey responses
            </li>
          </ul>

          <h3 data-id="element-1925">1.2 Information We Collect from Klaviyo (via OAuth)</h3>
          <p data-id="element-1926">
            When you connect your Klaviyo account, we request read-only access
            to specific data through secure OAuth 2.0 authentication. We
            collect:
          </p>
          <ul data-id="element-1927">
            <li data-id="element-1928">
              <strong data-id="element-1929">Flow Structure Data:</strong> Flow names, trigger types,
              email/SMS sequences, delays, conditional splits, and filter logic
            </li>
            <li data-id="element-1930">
              <strong data-id="element-1931">Template Data:</strong> Email subject lines, preview text,
              HTML content, and design elements (for analysis only)
            </li>
            <li data-id="element-1932">
              <strong data-id="element-1933">Performance Metrics:</strong> Aggregate metrics including
              sends, opens, clicks, conversions, and revenue (no individual
              recipient data)
            </li>
            <li data-id="element-1934">
              <strong data-id="element-1935">Event Configuration:</strong> Trigger event names and
              timing settings
            </li>
          </ul>
          <p className="bg-green-50 border-l-4 border-green-500 p-4 text-sm" data-id="element-1936">
            <strong data-id="element-1937">What We DO NOT Collect:</strong> We do not collect or store
            individual subscriber email addresses, phone numbers, names,
            purchase history, browsing behavior, or any other personally
            identifiable information about your customers. We only access
            aggregate performance data and flow configuration.
          </p>

          <h3 data-id="element-1938">1.3 Automatically Collected Information</h3>
          <ul data-id="element-1939">
            <li data-id="element-1940">
              <strong data-id="element-1941">Technical Data:</strong> IP address, browser type and
              version, device type, operating system, time zone, and referring
              URLs
            </li>
            <li data-id="element-1942">
              <strong data-id="element-1943">Usage Data:</strong> Pages visited, features used, time
              spent on pages, click patterns, and session recordings
              (anonymized)
            </li>
            <li data-id="element-1944">
              <strong data-id="element-1945">Cookies and Tracking:</strong> We use essential cookies
              for authentication and optional analytics cookies (with your
              consent)
            </li>
          </ul>

          <h2 data-id="element-1946">2. How We Use Your Information</h2>

          <h3 data-id="element-1947">2.1 To Provide and Improve Our Service</h3>
          <ul data-id="element-1948">
            <li data-id="element-1949">
              Analyze your Klaviyo flows and generate AI-powered recommendations
            </li>
            <li data-id="element-1950">
              Calculate health scores and identify optimization opportunities
            </li>
            <li data-id="element-1951">Display performance dashboards and reports</li>
            <li data-id="element-1952">
              Sync data from Klaviyo every 6 hours to keep insights current
            </li>
            <li data-id="element-1953">Improve our AI models and recommendation algorithms</li>
            <li data-id="element-1954">Develop new features and enhance existing functionality</li>
          </ul>

          <h3 data-id="element-1955">2.2 To Communicate with You</h3>
          <ul data-id="element-1956">
            <li data-id="element-1957">
              Send transactional emails (account verification, password resets,
              sync notifications)
            </li>
            <li data-id="element-1958">Provide customer support and respond to inquiries</li>
            <li data-id="element-1959">
              Send product updates, new feature announcements, and educational
              content (with opt-out)
            </li>
            <li data-id="element-1960">Request feedback and conduct user research</li>
          </ul>

          <h3 data-id="element-1961">2.3 For Business Operations</h3>
          <ul data-id="element-1962">
            <li data-id="element-1963">Process payments and manage subscriptions</li>
            <li data-id="element-1964">Prevent fraud and ensure platform security</li>
            <li data-id="element-1965">
              Comply with legal obligations and enforce our Terms of Service
            </li>
            <li data-id="element-1966">Analyze usage patterns to improve user experience</li>
          </ul>

          <h3 data-id="element-1967">2.4 Legal Basis for Processing (GDPR)</h3>
          <p data-id="element-1968">We process your personal data based on:</p>
          <ul data-id="element-1969">
            <li data-id="element-1970">
              <strong data-id="element-1971">Contract Performance:</strong> To provide the service you
              signed up for
            </li>
            <li data-id="element-1972">
              <strong data-id="element-1973">Legitimate Interests:</strong> To improve our service,
              prevent fraud, and ensure security
            </li>
            <li data-id="element-1974">
              <strong data-id="element-1975">Legal Compliance:</strong> To comply with applicable laws
              and regulations
            </li>
            <li data-id="element-1976">
              <strong data-id="element-1977">Consent:</strong> For marketing communications and
              optional analytics (which you can withdraw at any time)
            </li>
          </ul>

          <h2 data-id="element-1978">3. How We Share Your Information</h2>

          <h3 data-id="element-1979">3.1 Service Providers</h3>
          <p data-id="element-1980">
            We share data with trusted third-party service providers who help us
            operate our business:
          </p>
          <ul data-id="element-1981">
            <li data-id="element-1982">
              <strong data-id="element-1983">Stripe:</strong> Payment processing (PCI-DSS compliant)
            </li>
            <li data-id="element-1984">
              <strong data-id="element-1985">Amazon Web Services (AWS):</strong> Cloud hosting and data
              storage (encrypted)
            </li>
            <li data-id="element-1986">
              <strong data-id="element-1987">OpenAI:</strong> AI model API for generating
              recommendations (we send anonymized flow data only, no customer
              PII)
            </li>
            <li data-id="element-1988">
              <strong data-id="element-1989">PostHog:</strong> Product analytics (anonymized usage
              data)
            </li>
            <li data-id="element-1990">
              <strong data-id="element-1991">Resend:</strong> Transactional email delivery
            </li>
            <li data-id="element-1992">
              <strong data-id="element-1993">Sentry:</strong> Error monitoring and debugging
            </li>
          </ul>
          <p className="text-sm bg-gray-50 p-4 rounded" data-id="element-1994">
            All service providers are contractually obligated to protect your
            data and use it only for the purposes we specify. We conduct regular
            security reviews of all vendors.
          </p>

          <h3 data-id="element-1995">3.2 Business Transfers</h3>
          <p data-id="element-1996">
            If Flowsense is involved in a merger, acquisition, or sale of
            assets, your information may be transferred. We will provide notice
            before your data is transferred and becomes subject to a different
            privacy policy.
          </p>

          <h3 data-id="element-1997">3.3 Legal Requirements</h3>
          <p data-id="element-1998">
            We may disclose your information if required by law, court order, or
            government request, or if we believe disclosure is necessary to:
          </p>
          <ul data-id="element-1999">
            <li data-id="element-2000">Comply with legal obligations</li>
            <li data-id="element-2001">Protect our rights, property, or safety</li>
            <li data-id="element-2002">Prevent fraud or security threats</li>
            <li data-id="element-2003">Protect the rights and safety of our users</li>
          </ul>

          <h3 data-id="element-2004">3.4 What We Never Do</h3>
          <ul data-id="element-2005">
            <li data-id="element-2006">We never sell your personal data to third parties</li>
            <li data-id="element-2007">We never share your Klaviyo data with other Flowsense users</li>
            <li data-id="element-2008">We never use your data for advertising purposes</li>
            <li data-id="element-2009">
              We never share individual customer data from your Klaviyo account
            </li>
          </ul>

          <h2 data-id="element-2010">4. Data Security</h2>

          <h3 data-id="element-2011">4.1 Security Measures</h3>
          <p data-id="element-2012">
            We implement industry-standard security measures to protect your
            data:
          </p>
          <ul data-id="element-2013">
            <li data-id="element-2014">
              <strong data-id="element-2015">Encryption:</strong> All data is encrypted in transit (TLS
              1.3) and at rest (AES-256)
            </li>
            <li data-id="element-2016">
              <strong data-id="element-2017">Access Controls:</strong> Role-based access control (RBAC)
              and principle of least privilege
            </li>
            <li data-id="element-2018">
              <strong data-id="element-2019">Authentication:</strong> Secure password hashing (bcrypt),
              optional two-factor authentication (2FA)
            </li>
            <li data-id="element-2020">
              <strong data-id="element-2021">OAuth Security:</strong> Klaviyo connections use OAuth 2.0
              with token encryption and automatic expiration
            </li>
            <li data-id="element-2022">
              <strong data-id="element-2023">Infrastructure:</strong> AWS infrastructure with DDoS
              protection, firewalls, and intrusion detection
            </li>
            <li data-id="element-2024">
              <strong data-id="element-2025">Monitoring:</strong> 24/7 security monitoring, automated
              threat detection, and incident response procedures
            </li>
            <li data-id="element-2026">
              <strong data-id="element-2027">Backups:</strong> Daily encrypted backups with 30-day
              retention
            </li>
            <li data-id="element-2028">
              <strong data-id="element-2029">Audits:</strong> Regular security audits and penetration
              testing
            </li>
          </ul>

          <h3 data-id="element-2030">4.2 Data Isolation</h3>
          <p data-id="element-2031">
            Each client's data is logically isolated in our database. Agency
            users can only access data for clients they have explicit
            permissions for. We use row-level security and strict access
            controls to prevent unauthorized data access.
          </p>

          <h3 data-id="element-2032">4.3 Employee Access</h3>
          <p data-id="element-2033">
            Access to customer data is restricted to authorized employees who
            need it to provide support or maintain the service. All employees
            sign confidentiality agreements and receive security training.
          </p>

          <h2 data-id="element-2034">5. Data Retention</h2>

          <h3 data-id="element-2035">5.1 Active Accounts</h3>
          <p data-id="element-2036">
            We retain your account data and Klaviyo sync data for as long as
            your account is active and for 90 days after account closure (to
            allow for reactivation).
          </p>

          <h3 data-id="element-2037">5.2 Deleted Accounts</h3>
          <p data-id="element-2038">When you delete your account, we permanently delete:</p>
          <ul data-id="element-2039">
            <li data-id="element-2040">Your account information within 30 days</li>
            <li data-id="element-2041">All Klaviyo flow data and recommendations within 30 days</li>
            <li data-id="element-2042">OAuth tokens immediately</li>
          </ul>
          <p data-id="element-2043">
            We may retain certain data longer if required by law (e.g.,
            financial records for tax purposes) or for legitimate business
            purposes (e.g., anonymized analytics data).
          </p>

          <h3 data-id="element-2044">5.3 Backup Retention</h3>
          <p data-id="element-2045">
            Deleted data may persist in encrypted backups for up to 30 days
            before being permanently purged.
          </p>

          <h2 data-id="element-2046">6. Your Privacy Rights</h2>

          <h3 data-id="element-2047">6.1 Rights for All Users</h3>
          <ul data-id="element-2048">
            <li data-id="element-2049">
              <strong data-id="element-2050">Access:</strong> Request a copy of your personal data
            </li>
            <li data-id="element-2051">
              <strong data-id="element-2052">Correction:</strong> Update or correct inaccurate
              information
            </li>
            <li data-id="element-2053">
              <strong data-id="element-2054">Deletion:</strong> Request deletion of your account and
              data
            </li>
            <li data-id="element-2055">
              <strong data-id="element-2056">Export:</strong> Download your data in a portable format
              (JSON)
            </li>
            <li data-id="element-2057">
              <strong data-id="element-2058">Opt-Out:</strong> Unsubscribe from marketing emails
            </li>
          </ul>

          <h3 data-id="element-2059">6.2 Additional Rights for EU/UK Users (GDPR)</h3>
          <ul data-id="element-2060">
            <li data-id="element-2061">
              <strong data-id="element-2062">Right to Restriction:</strong> Request we limit how we use
              your data
            </li>
            <li data-id="element-2063">
              <strong data-id="element-2064">Right to Object:</strong> Object to processing based on
              legitimate interests
            </li>
            <li data-id="element-2065">
              <strong data-id="element-2066">Right to Portability:</strong> Receive your data in a
              machine-readable format
            </li>
            <li data-id="element-2067">
              <strong data-id="element-2068">Right to Withdraw Consent:</strong> Withdraw consent for
              marketing or analytics at any time
            </li>
            <li data-id="element-2069">
              <strong data-id="element-2070">Right to Lodge a Complaint:</strong> File a complaint with
              your local data protection authority
            </li>
          </ul>

          <h3 data-id="element-2071">6.3 Additional Rights for California Users (CCPA)</h3>
          <ul data-id="element-2072">
            <li data-id="element-2073">
              <strong data-id="element-2074">Right to Know:</strong> Request details about data we
              collect, use, and share
            </li>
            <li data-id="element-2075">
              <strong data-id="element-2076">Right to Delete:</strong> Request deletion of your
              personal information
            </li>
            <li data-id="element-2077">
              <strong data-id="element-2078">Right to Opt-Out:</strong> Opt out of "sale" of personal
              information (we do not sell data)
            </li>
            <li data-id="element-2079">
              <strong data-id="element-2080">Right to Non-Discrimination:</strong> We will not
              discriminate against you for exercising your rights
            </li>
          </ul>

          <h3 data-id="element-2081">6.4 How to Exercise Your Rights</h3>
          <p data-id="element-2082">
            To exercise any of these rights, contact us at{' '}
            <a href="mailto:support@useflowsense.com" data-id="element-2083">
              support@useflowsense.com
            </a>{' '}
            or use the data export/deletion tools in your account settings. We
            will respond within 30 days.
          </p>

          <h2 data-id="element-2084">7. International Data Transfers</h2>
          <p data-id="element-2085">
            Flowsense is operated by Acciom Limited in the United Kingdom. Your
            data may be transferred to and processed in the UK, EU, and United
            States (AWS data centers). We ensure adequate protection through:
          </p>
          <ul data-id="element-2086">
            <li data-id="element-2087">
              Standard Contractual Clauses (SCCs) approved by the EU Commission
            </li>
            <li data-id="element-2088">AWS's compliance with EU-U.S. Data Privacy Framework</li>
            <li data-id="element-2089">Encryption and security measures equivalent to EU standards</li>
          </ul>

          <h2 data-id="element-2090">8. Children's Privacy</h2>
          <p data-id="element-2091">
            Flowsense is not intended for users under 16 years of age. We do not
            knowingly collect personal information from children. If we discover
            we have collected data from a child, we will delete it immediately.
          </p>

          <h2 data-id="element-2092">9. Cookies and Tracking Technologies</h2>

          <h3 data-id="element-2093">9.1 Essential Cookies</h3>
          <p data-id="element-2094">
            Required for authentication and core functionality (cannot be
            disabled):
          </p>
          <ul data-id="element-2095">
            <li data-id="element-2096">
              <strong data-id="element-2097">Session cookies:</strong> Keep you logged in
            </li>
            <li data-id="element-2098">
              <strong data-id="element-2099">Security cookies:</strong> Prevent CSRF attacks
            </li>
            <li data-id="element-2100">
              <strong data-id="element-2101">Preference cookies:</strong> Remember your settings
            </li>
          </ul>

          <h3 data-id="element-2102">9.2 Analytics Cookies (Optional)</h3>
          <p data-id="element-2103">
            Help us understand how users interact with Flowsense (can be
            disabled):
          </p>
          <ul data-id="element-2104">
            <li data-id="element-2105">
              <strong data-id="element-2106">PostHog:</strong> Product analytics and feature usage
            </li>
            <li data-id="element-2107">
              <strong data-id="element-2108">Google Analytics:</strong> Website traffic and user
              behavior
            </li>
          </ul>

          <h3 data-id="element-2109">9.3 Managing Cookies</h3>
          <p data-id="element-2110">
            You can manage cookie preferences in your account settings or
            browser settings. Disabling analytics cookies will not affect core
            functionality.
          </p>

          <h2 data-id="element-2111">10. Third-Party Links</h2>
          <p data-id="element-2112">
            Our service may contain links to third-party websites (e.g.,
            Klaviyo, Stripe). We are not responsible for the privacy practices
            of these sites. We encourage you to read their privacy policies.
          </p>

          <h2 data-id="element-2113">11. Changes to This Privacy Policy</h2>
          <p data-id="element-2114">
            We may update this privacy policy from time to time. We will notify
            you of material changes by:
          </p>
          <ul data-id="element-2115">
            <li data-id="element-2116">
              Posting the new policy on this page with an updated "Last updated"
              date
            </li>
            <li data-id="element-2117">
              Sending an email notification to your registered email address
            </li>
            <li data-id="element-2118">Displaying an in-app notification</li>
          </ul>
          <p data-id="element-2119">
            Continued use of Flowsense after changes constitutes acceptance of
            the updated policy.
          </p>

          <h2 data-id="element-2120">12. Contact Us</h2>
          <p data-id="element-2121">
            If you have questions, concerns, or requests regarding this privacy
            policy or our data practices, please contact us:
          </p>
          <div className="bg-gray-50 p-6 rounded-lg not-prose" data-id="element-2122">
            <p className="font-semibold text-gray-900 mb-2" data-id="element-2123">
              Flowsense by Acciom Limited
            </p>
            <p className="text-gray-700 text-sm mb-1" data-id="element-2124">
              <strong data-id="element-2125">General Inquiries:</strong>{' '}
              <a href="mailto:support@useflowsense.com" className="text-indigo-600 hover:underline" data-id="element-2126">
                support@useflowsense.com
              </a>
            </p>
            <p className="text-gray-700 text-sm" data-id="element-2127">
              <strong data-id="element-2128">Data Protection Officer:</strong>{' '}
              <a href="mailto:support@useflowsense.com" className="text-indigo-600 hover:underline" data-id="element-2129">
                support@useflowsense.com
              </a>
            </p>
          </div>

          <div className="bg-indigo-50 border-l-4 border-indigo-500 p-6 mt-8" data-id="element-2130">
            <p className="text-sm text-indigo-900 mb-2" data-id="element-2131">
              <strong data-id="element-2132">Your Privacy Matters</strong>
            </p>
            <p className="text-sm text-indigo-800 mb-0" data-id="element-2133">
              We are committed to transparency and protecting your privacy. If
              you have any concerns about how we handle your data, please don't
              hesitate to reach out. We're here to help.
            </p>
          </div>
        </div>
      </Container>
    </div>;
}